import { Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  CanActivate,
  CanLoad,
  Route,
  UrlSegment,
  CanActivateChild,
} from "@angular/router";
import {
  Portals,
  QueryParameters,
} from "apps/patient-reporting/src/app/shared/enums/query.enums";
import { getQueryParamsFromLink } from "apps/patient-reporting/src/app/shared/functions/get-query.functions";
import { SharedService } from "apps/patient-reporting/src/app/shared/shared.service";
import { Observable, of } from "rxjs";
import { tap, take } from "rxjs/operators";
import { IntegratedAuthService } from "../auth";
import { SubscriptionPlan } from "@kells/interfaces/patient";
import { VirtualCheckupHandoffService } from "../virtual-checkup-handoff/virtual-checkup-handoff.service";

@Injectable({
  providedIn: "root",
})
export class AuthGuard implements CanActivate, CanLoad, CanActivateChild {
  constructor(
    private auth: IntegratedAuthService,
    private sharedService: SharedService,
    private handoff: VirtualCheckupHandoffService
  ) {}

  canLoad(route: Route, segments: UrlSegment[]): Observable<boolean> {
    return this.auth.isAuthenticated$.pipe(take(1));
  }

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    return this.redirectIfUnauthenticated(state);
  }

  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    return this.redirectIfUnauthenticated(state);
  }

  private redirectIfUnauthenticated(
    state: RouterStateSnapshot
  ): Observable<boolean> {
    if (this.handoff.isActive()) {
      this.applyQueryPortalAndPlan();
      return of(true);
    }
    return this.auth.isAuthenticated$.pipe(
      tap((loggedIn) => {
        this.applyQueryPortalAndPlan();

        if (!loggedIn) {
          this.auth.loginWithRedirect({
            screen_hint: "signup",
            appState: { target: state.url },
          });
        }
      })
    );
  }

  private applyQueryPortalAndPlan(): void {
    const queryPortal = getQueryParamsFromLink(window.location.href)?.[
      QueryParameters.Portal
    ] as Portals;
    this.sharedService.localStoragePortal =
      queryPortal || this.sharedService.localStoragePortal;

    const queryPlan = getQueryParamsFromLink(window.location.href)?.[
      QueryParameters.Plan
    ] as SubscriptionPlan;
    this.sharedService.localStoragePlan =
      queryPlan || this.sharedService.localStoragePlan;
  }
}
